import React, { useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { EMAIL_PATTERN } from '../validation/patterns'

// Hoisted style objects — avoids re-creation on every render (rendering-hoist-jsx)
const styles = {
    minHeight: { minHeight: '30px' },
    required: { color: 'red' },
    emailLabel: { textAlign: 'left', width: 'auto', marginRight: '0' },
    fullWidth: { width: '100%' },
}

function getStoredJson(key, fallback = {}) {
    try {
        const raw = window.localStorage.getItem(key)
        return raw ? JSON.parse(raw) : fallback
    } catch {
        return fallback
    }
}

function safeSetItem(key, value) {
    try {
        window.localStorage.setItem(key, value)
    } catch {
        // Fails in incognito/Safari/quota-exceeded — non-critical
    }
}

export default function AdditionalPatientServices() {
    const { enrollment_data, setenrollmentdata, email_opt_in, setemailopt } = useOutletContext()

    const [optIn, setOptIn] = useState(() => {
        if (email_opt_in?.opt) return email_opt_in.opt
        return window.localStorage.getItem('emailoptin') || ''
    })

    const [emailAddress, setEmailAddress] = useState(() => {
        if (enrollment_data?.patient_services_email) return enrollment_data.patient_services_email
        return getStoredJson('enrollment_data')?.patient_services_email || ''
    })

    // Simple expression with primitive result — no useMemo needed (rerender-simple-expression-in-memo)
    const emailValidationMsg =
        (optIn === 'Y' && (emailAddress === '' || !EMAIL_PATTERN.test(emailAddress)))
            ? 'Please Enter Valid Email'
            : ''

    function handleOptInChange(e) {
        const value = e.target.value
        setOptIn(value)

        const updatedOptIn = { ...email_opt_in, opt: value }
        setemailopt(updatedOptIn)
        safeSetItem('email_opt_in', JSON.stringify(updatedOptIn))
        safeSetItem('emailoptin', value)

        // Always clear email on opt-in toggle so user must re-enter when selecting 'Yes' again
        setEmailAddress('')
        const updatedEnrollment = { ...enrollment_data, patient_services_email: '' }
        setenrollmentdata(updatedEnrollment)
        safeSetItem('enrollment_data', JSON.stringify(updatedEnrollment))
        safeSetItem('email_validation', JSON.stringify({ email_validations: '' }))
    }

    function handleEmailChange(e) {
        const newEmail = e.target.value
        setEmailAddress(newEmail)

        const updatedEnrollment = { ...enrollment_data, patient_services_email: newEmail }
        setenrollmentdata(updatedEnrollment)
        safeSetItem('enrollment_data', JSON.stringify(updatedEnrollment))

        const validationMsg = (optIn === 'Y' && (newEmail === '' || !EMAIL_PATTERN.test(newEmail)))
            ? 'Please Enter Valid Email'
            : ''
        safeSetItem('email_validation', JSON.stringify({ email_validations: validationMsg }))
    }

    return (
        <>
            <div className="section shadow-box">
                <div className="col-sm-12 col-md-12 col-lg-12">
                    <h2>Sign Up for Additional Patient Services</h2>
                    <p style={styles.minHeight}>
                        <span style={styles.required}><sup>*</sup></span> Required field if you wish to sign up for this optional and free patient support program
                    </p>
                    <p style={styles.minHeight}>
                        In addition to the XOLAIR Co-pay Program, XOLAIR offers an optional and free patient support program.{' '}
                        <span style={styles.required}><sup>*</sup></span>You can get tools and resources to help stay motivated and in the know about what to expect next when you are on XOLAIR.
                    </p>
                    <p style={styles.minHeight}>
                        Would the patient like to enroll in the patient support program?<span style={styles.required}><sup>*</sup></span>
                    </p>
                    <p>
                        <label className="radio">
                            <input
                                onChange={handleOptInChange}
                                type="radio"
                                id="enroll-yes"
                                name="enroll_email"
                                value="Y"
                                checked={optIn === 'Y'}
                            />{' '}Yes <span className="checkmark"></span>
                        </label>
                        <label className="radio">
                            <input
                                onChange={handleOptInChange}
                                type="radio"
                                name="enroll_email"
                                id="enroll-no"
                                value="N"
                                checked={optIn === 'N'}
                            />{' '}No <span className="checkmark"></span>
                        </label>
                    </p>
                    {/* id="hide-yes" kept for jQuery compatibility in parent pages */}
                    <div id="hide-yes" className={optIn === 'Y' ? '' : 'hide'}>
                        <div className="col-sm-12 col-md-12">
                            <p className="select-form">
                                <label style={styles.emailLabel}>
                                    {' '}Email address
                                </label>:<span style={styles.required}><sup>&dagger;</sup></span><br />
                                <input
                                    onChange={handleEmailChange}
                                    style={styles.fullWidth}
                                    type="email"
                                    name="email_yes"
                                    id="email-yes"
                                    value={emailAddress}
                                />
                            </p>
                            {emailValidationMsg && <p style={styles.required}>{emailValidationMsg}</p>}
                        </div>
                        <p className="checkbox-field">
                            <label style={styles.fullWidth} className="checkbox">
                                <input id="terms-checkbox" type="checkbox" name="agree" value="yes" />
                                By checking this box, I am requesting to receive optional disease education and other materials offered by Genentech and its affiliates. I understand that my personal information, some of which may be considered sensitive information, such as information about health conditions, will be used and disclosed as described in Genentech's{' '}
                                <a href="http://www.gene.com/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>, and that I may be contacted using the information I have provided. I understand providing this agreement is voluntary and plays no role in getting my medicine, and that I may opt out of receiving this information at any time by calling 877-436-3683. By submitting this form, I confirm that I am 18 years of age or older and that I agree to the{' '}
                                <a href="https://www.gene.com/terms-conditions" target="_blank" rel="noreferrer">Terms &amp; Conditions</a>.
                                <span className="checkmark "></span>
                            </label>
                        </p>
                        <p><span style={styles.required}><sup>†</sup></span> <i>Your email address is required to participate in this optional and free patient support program.</i></p>
                        <p>&nbsp;</p>
                    </div>
                </div>
            </div>
        </>
    )
}

